import * as React from 'react';
import './TextInput.scss';
import eyeOpen from '../../../assets/EyeOpen.png';
import eyeClosed from '../../../assets/EyeClosed.png';

interface Props {
  label: string;
  type: 'text' | 'password' | 'email';
  onChange: (value: string) => void;
  onClick?: () => void;
  onEnterKey?: () => void;
  onFocus?: () => void;
  value: string;
  id: string;
  error?: boolean;
  errorMessage?: string;
  autofocus?: boolean;
  disabled?: boolean;
  leftPartComponent?: any;
  mandatory?: boolean;
  autoComplete?: boolean;
  maxLength?: number;
}

interface State {
  hasFocus: boolean;
  showPassword: boolean;
  autoFilled: boolean;
}

export default class TextInput extends React.Component<Props, State> {
  state: State = { hasFocus: false, showPassword: false, autoFilled: false };
  ref: React.RefObject<HTMLInputElement>;

  constructor(props: Props) {
    super(props);
    this.ref = React.createRef();
  }

  componentDidMount() {
    // This is a hack to go around the chrome issue not allowing us to get the value of
    // an autocompleted field before the user interacts with the page
    if (this.ref.current) {
      this.ref.current.addEventListener('animationstart', this.onAnimationStart, false);
    }
  }

  componentWillUnmount() {
    if (this.ref.current) {
      this.ref.current.removeEventListener('animationstart', this.onAnimationStart);
    }
  }

  onAutoFillStart = () => this.setState({ autoFilled: true });

  onAutoFillCancel = () => this.setState({ autoFilled: false });

  onAnimationStart = ({ _target, animationName }: any) => {
    switch (animationName) {
      case 'onAutoFillStart':
        return this.onAutoFillStart();
      case 'onAutoFillCancel':
        return this.onAutoFillCancel();
    }
  };

  handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.props.onChange(e.target.value);
  };

  handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
    if (this.props.onClick) {
      this.props.onClick();
    }
  };

  handleFocus = () => {
    this.setState({ hasFocus: true });
    if (this.props.onFocus) {
      this.props.onFocus();
    }
  };

  handleBlur = () => {
    this.setState({ hasFocus: false });
  };

  toggleShowPassword = () => {
    this.setState((state) => ({ showPassword: !state.showPassword }));
  };

  onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter' && this.props.onEnterKey) {
      event.preventDefault();
      event.stopPropagation();
      this.props.onEnterKey();
    }
  };

  public render() {
    const {
      type,
      label,
      value,
      id,
      error = false,
      autofocus = false,
      disabled = false,
      mandatory = true,
      leftPartComponent = null,
    } = this.props;
    const { hasFocus, showPassword, autoFilled } = this.state;
    const smallLabel = hasFocus || value !== '' || autoFilled;
    const hasLeftPartComponent = leftPartComponent !== null;

    const inputType = (type: string) => {
      if(type==='email'){
        return 'email'
      }
      return type === 'password' && !showPassword ? 'password' : 'text'
    };
    return (
      <div
        className={'field ' + (error ? 'error ' : '' + (disabled ? 'disabled ' : ''))}
        onMouseDown={this.handleClick}
      >
        <label
          className={(smallLabel ? 'small ' : '') + (hasLeftPartComponent ? 'with-left-part' : '')}
          htmlFor={id}
        >
          {label}
          {this.props.mandatory ? <em className="text-red"> *</em> : ''}
        </label>
        {hasLeftPartComponent && <div className="left-part">{leftPartComponent}</div>}
        <input
          ref={this.ref}
          type={inputType(type)}
          value={value}
          onChange={this.handleChange}
          onBlur={this.handleBlur}
          onFocus={this.handleFocus}
          onKeyDown={this.onKeyDown}
          autoFocus={autofocus}
          disabled={disabled}
          autoComplete={this.props.autoComplete === false ? 'new-password' : undefined}
          className={ (hasLeftPartComponent ? 'with-left-part' : '') +(error ? 'error ' : '' )}
          maxLength={this.props.maxLength}
          required={mandatory}
          id={id}
        />
        {type === 'password' && (
          <div
            className={`img ${showPassword ? 'eye-open' : 'eye-closed'}`}
            onClick={this.toggleShowPassword}
          >
            <img src={showPassword ? eyeOpen : eyeClosed} alt="Toggle visibility" />
          </div>
        )}
      </div>
    );
  }
}
